import { CheckOutlined, CopyOutlined } from '@ant-design/icons'
import { Card, Descriptions, message, Spin, Tooltip, Typography } from 'antd'
import { useState } from 'react'

import { MONO_STYLE } from '@/components/common/MonoId'
import { useSafeName } from '@/hooks/useSafeName'

type Kind = 'vpc' | 'subnet' | 'storage' | 'vm'

interface Command {
  caption: string
  cmd: string
}

export function TroubleshootCard({
  kind,
  userId,
  resourceId,
}: {
  kind: Kind
  userId: string
  resourceId: string
}) {
  const workspaceName = `workspace-${userId}`
  const namespace = useSafeName('workspace', [workspaceName])
  const name = useSafeName(kind, [workspaceName, `${kind}-${resourceId}`])

  return (
    <Card title="Troubleshoot">
      {!namespace || !name ? (
        <Spin />
      ) : (
        <>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Namespace">
              <Typography.Text copyable style={MONO_STYLE}>
                {namespace}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Resource name">
              <Typography.Text copyable style={MONO_STYLE}>
                {name}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
            {buildCommands(kind, namespace, name).map(({ caption, cmd }) => (
              <CommandLine key={cmd} caption={caption} cmd={cmd} />
            ))}
          </div>
        </>
      )}
    </Card>
  )
}

function CommandLine({ caption, cmd }: Command) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cmd)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      void message.error('Failed to copy')
    }
  }

  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: 'rgba(255, 255, 255, 0.65)',
          marginBottom: 2,
        }}
      >
        {caption}
      </div>
      <Tooltip title={copied ? 'Copied' : 'Click to copy'}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => void copy()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              void copy()
            }
          }}
          style={{
            ...MONO_STYLE,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '6px 10px',
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            userSelect: 'all',
          }}
        >
          <span style={{ flex: 1, wordBreak: 'break-all' }}>{cmd}</span>
          {copied ? (
            <CheckOutlined style={{ color: '#52c41a' }} />
          ) : (
            <CopyOutlined style={{ opacity: 0.6 }} />
          )}
        </div>
      </Tooltip>
    </div>
  )
}

function buildCommands(kind: Kind, ns: string, name: string): Command[] {
  const nsEvents: Command = {
    caption: 'Events for this resource',
    cmd: `kubectl get events -n ${ns} --field-selector involvedObject.name=${name}`,
  }
  const allEvents: Command = {
    caption: 'Events for this resource (all namespaces)',
    cmd: `kubectl get events -A --field-selector involvedObject.name=${name}`,
  }
  const allInNs: Command = {
    caption: 'Everything in the workspace namespace',
    cmd: `kubectl get all -n ${ns}`,
  }
  switch (kind) {
    case 'vm':
      return [
        {
          caption: 'Get the KubeVirt VM',
          cmd: `kubectl get vm -n ${ns} ${name}`,
        },
        {
          caption: 'Describe the KubeVirt VM',
          cmd: `kubectl describe vm -n ${ns} ${name}`,
        },
        {
          caption: 'Get the running VM instance',
          cmd: `kubectl get vmi -n ${ns} ${name}`,
        },
        {
          caption: 'Tail the virt-launcher pod logs',
          cmd: `kubectl logs -n ${ns} -l kubevirt.io/vm=${name}`,
        },
        nsEvents,
        allInNs,
      ]
    case 'storage':
      return [
        { caption: 'Get the PVC', cmd: `kubectl get pvc -n ${ns} ${name}` },
        {
          caption: 'Describe the PVC',
          cmd: `kubectl describe pvc -n ${ns} ${name}`,
        },
        nsEvents,
        allInNs,
      ]
    case 'vpc':
      return [
        {
          caption: 'Get the kube-ovn VPC (cluster-scoped)',
          cmd: `kubectl get vpc.kubeovn.io ${name}`,
        },
        {
          caption: 'Describe the kube-ovn VPC',
          cmd: `kubectl describe vpc.kubeovn.io ${name}`,
        },
        allEvents,
        allInNs,
      ]
    case 'subnet':
      return [
        {
          caption: 'Get the kube-ovn Subnet (cluster-scoped)',
          cmd: `kubectl get subnet.kubeovn.io ${name}`,
        },
        {
          caption: 'Describe the kube-ovn Subnet',
          cmd: `kubectl describe subnet.kubeovn.io ${name}`,
        },
        allEvents,
        allInNs,
      ]
  }
}
